import { z } from 'zod';
import { IAddress } from './address';
import { IAsset } from './asset';
import { CurrencyCode } from './base';
import { SalesChannelType } from './saleschannel';
import { ISKU } from './skus';
import type { HandoverMode, ISellerLeg } from './handover';

export enum CourierPartner {
  SELF = 'SELF',
  SHIPROCKET = 'SHIPROCKET',
  SHIPROCKET_QUICK = 'SHIPROCKET_QUICK',
  SHIPROCKET_AIR = 'SHIPROCKET_AIR',
  PORTER = 'PORTER',
  SHIPROCKET_STANDARD = 'SHIPROCKET_STANDARD',
  SELF_EXPRESS = 'SELF_EXPRESS',
  SELF_PRIORITY = 'SELF_PRIORITY',
  INTERNATIONAL = 'INTERNATIONAL',
  LYSKRAFT = 'LYSKRAFT',
  DTDC = 'DTDC',
  BLUEDART = 'BLUEDART',
  BLUEDART_BLR = 'BLUEDART_BLR',
  BLUEDART_MUM = 'BLUEDART_MUM',
  BLUEDART_SURFACE = 'BLUEDART_SURFACE',
  BLUEDART_PRIORITY = 'BLUEDART_PRIORITY',
  BLUEDART_TDD = 'BLUEDART_TDD',
  ESHIPZ = 'ESHIPZ',
  SHADOWFAX = 'SHADOWFAX',
  TRACKON = 'TRACKON',
  MERCHANTS_COURIER = 'MERCHANTS_COURIER',
  SHREE_MARUTI = 'SHREE_MARUTI',
}

export type OrderLineActionCode =
  | 'alteration_complete'
  | 'qc_fail_reship'
  | 'customization_review_done'
  | 'brand_accepted'
  | 'brand_declined'
  | 'brand_status_updated'
  | 'brand_dispatched'
  | 'b2b_po_received_at_warehouse'
  | 'cancel'
  | 'b2b2c_brand_cancel'
  | 'return'
  | 'exchange';

export enum OrderLineOnHoldReason {
  NA = 'NA',
  PENDING_ALTERATION = 'PENDING_ALTERATION',
  PENDING_CUSTOMISATION_REMARKS = 'PENDING_CUSTOMISATION_REMARKS',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  COD_CONFIRMATION_PENDING = 'COD_CONFIRMATION_PENDING',
  RETURN_PENDING = 'RETURN_PENDING',
  FREQUENT_RETURNER = 'FREQUENT_RETURNER',
  TRY_AT_HOME_CONSOLIDATION = 'TRY_AT_HOME_CONSOLIDATION',
  UNICOM_DATA_VALIDATION_ERR = 'UNICOM_DATA_VALIDATION_ERR',
}

export enum RefundMode {
  STORE_CREDIT = 'STORE_CREDIT',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  CASH = 'CASH',
  UPI = 'UPI',
  SOURCE = 'SOURCE',
  NONE = 'NONE',
  OTHERS = 'OTHERS',
}

export enum PackageStatus {
  CREATED = 'CREATED',
  FULFILLABLE = 'FULFILLABLE',
  WIP = 'WIP',
  PACKED = 'PACKED',
  DISPATCHED = 'DISPATCHED',
  DELIVERED_AT_STORE = 'DELIVERED_AT_STORE',
  RECEIVED_AT_STORE = 'RECEIVED_AT_STORE',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  CANCELLED = 'CANCELLED',
  RTO_INITIATED = 'RTO_INITIATED',
  RTO = 'RTO',
  DELIVERED = 'DELIVERED',
}

export enum RefundRequestStatus {
  CREATED = 'CREATED',
  INITIATED = 'INITIATED',
  PROCESSED = 'PROCESSED',
  FAILED = 'FAILED',
  DISCARDED = 'DISCARDED',
}

export enum RefundTriggerAction {
  CANCEL = 'CANCEL',
  RETURN = 'RETURN',
  RTO = 'RTO',
  EXCHANGE = 'EXCHANGE',
  EXPRESS_SHIPPING_DELAY = 'EXPRESS_SHIPPING_DELAY',
}

export enum PaymentStatus {
  PAID = 'PAID',
  PENDING = 'PENDING',
}

export enum PaymentMethod {
  COD = 'COD',
  PREPAID = 'PREPAID',
}

export enum RiderCashHandoverStatus {
  PENDING_RIDER_CONFIRMATION = 'PENDING_RIDER_CONFIRMATION',
  PENDING_HUB_CONFIRMATION = 'PENDING_HUB_CONFIRMATION',
  COMPLETED = 'COMPLETED',
  DISPUTED = 'DISPUTED',
  CANCELLED = 'CANCELLED',
}

export enum RiderCashHandoverItemStatus {
  PENDING = 'PENDING',
  RIDER_CONFIRMED = 'RIDER_CONFIRMED',
  COMPLETED = 'COMPLETED',
  DISPUTED = 'DISPUTED',
  CANCELLED = 'CANCELLED',
}

export enum RiderCashHandoverProvider {
  LYSKRAFT_RIDER = 'LYSKRAFT_RIDER',
  SHADOWFAX = 'SHADOWFAX',
}

export enum RiderTrackingStatus {
  TRACKING_OK = 'TRACKING_OK',
  STALE_PING = 'STALE_PING',
  PERMISSION_REVOKED = 'PERMISSION_REVOKED',
  BATTERY_OPTIMIZED_OR_KILLED = 'BATTERY_OPTIMIZED_OR_KILLED',
  APP_FORCE_STOPPED_OR_NO_RECOVERY_PING = 'APP_FORCE_STOPPED_OR_NO_RECOVERY_PING',
  DEVICE_CHANGED = 'DEVICE_CHANGED',
  OFFLINE_BUFFERING = 'OFFLINE_BUFFERING',
  NOTIFICATION_DEGRADED = 'NOTIFICATION_DEGRADED',
  UNKNOWN = 'UNKNOWN',
}

export interface IRiderLatestState {
  id: number;
  riderId: number;
  deviceId?: string;
  app?: string;
  os?: string;
  appVersion?: string;
  sessionId?: string;
  trackingStatus: RiderTrackingStatus;
  lastPingAt: string;
  lastEventAt?: string | null;
  lastHealthAt?: string | null;
  lastLocationAt?: string | null;
  lastFacilityArrivalAt?: string | null;
  lastFacilityArrivalFacilityId?: number | null;
  lastFacilityArrivalDistanceKm?: number | null;
  lastFacilityArrivalStatus?: string | null;
  lastReceivedAt: string;
  lastEventId: string;
  lastEventName: string;
  eventSequence?: number;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  batteryPercent?: number;
  charging?: boolean;
  foregroundServiceRunning?: boolean;
  locationPermission?: string;
  notificationPermission?: string;
  batteryOptimizationIgnored?: boolean;
  activeShiftId?: number;
  activePackageId?: number;
  activeReturnId?: number;
  metadata?: Record<string, unknown>;
}

export interface IRiderControlCentreWork {
  workType: 'PACKAGE' | 'RETURN' | 'TRY_AT_HOME' | string;
  workId: number;
  orderId?: number;
  salesChannelOrderId?: string;
  status?: string;
  deliveryMode?: string;
  fulfillmentMode?: string;
  tryAtHome?: boolean;
  tryAtHomeSlot?: string;
  express?: boolean;
  facilityId?: number;
  receivingFacilityId?: number;
  expectedBy?: string;
  promisedDeliveryAt?: string;
  estimatedDeliveryAt?: string;
  statusSince?: string;
  createdOn?: string;
  targetType?: string;
  inFlight?: boolean;
}

export interface IRiderControlCentreRider {
  riderId: number;
  operatingFacility?: {
    id?: number;
    name?: string;
    source?: string;
    coordinatesAvailable?: boolean;
  };
  shift?: {
    id: number;
    status: string;
    facilityId?: number;
    submittedAt?: string | null;
    startedAt?: string | null;
    endedAt?: string;
    attendanceMethod?: string;
    attendanceReviewStatus?: string;
  };
  attendance?: {
    status:
      | 'PENDING_APPROVAL'
      | 'ATTENDANCE_REJECTED'
      | 'ON_SHIFT'
      | 'SHIFT_COMPLETED'
      | 'NOT_STARTED'
      | 'UNAVAILABLE'
      | string;
    statusLabel: string;
    firstShiftStartedAt?: string;
    activeShiftStartedAt?: string;
    lastShiftEndedAt?: string;
    shiftCount?: number;
    pendingSelfieCount?: number;
    rejectedSelfieCount?: number;
    approvedSelfieCount?: number;
  };
  operationalState: string;
  operationalStateLabel: string;
  operationalBucket?:
    | 'AVAILABLE'
    | 'ON_JOB'
    | 'AWAY'
    | 'ASSIGNED'
    | 'OFF_SHIFT'
    | 'UNAVAILABLE'
    | string;
  operationalBucketLabel?: string;
  latestState?: IRiderControlCentreDeviceState;
  workload?: {
    forward: number;
    tryAtHome: number;
    returns: number;
    total: number;
    inFlight: number;
    overdue?: number;
    staleAssignments?: number;
    oldestExpectedBy?: string;
  };
  currentWork?: IRiderControlCentreWork;
  priorityWork?: IRiderControlCentreWork;
  reportedActiveWork?: IRiderControlCentreWork;
  reportedActiveWorkMatchStatus?:
    | 'MATCHED'
    | 'NOT_REPORTED'
    | 'STALE_REFERENCE'
    | 'AMBIGUOUS'
    | 'NO_IN_FLIGHT_WORK'
    | 'UNAVAILABLE'
    | string;
  expectation?: {
    expectedBy?: string;
    source?: string;
    overdue?: boolean;
  };
  movement?: {
    targetType?: string;
    haversineDistanceKm?: number;
    facilityDistanceKm?: number;
    workTargetDistanceKm?: number;
    workTargetType?: string;
    workType?: string;
    workId?: number;
    atFacility?: boolean;
    evidence?: string;
    lastLocationAt?: string;
    locationAccuracyMeters?: number;
  };
  cash?: {
    unlinkedAmount: number;
    activeHandoverAmount: number;
    disputedAmount: number;
    totalAmount: number;
    openHandoverCount: number;
    hasDispute: boolean;
    state?: 'NONE' | 'WITH_RIDER' | 'HANDOVER_PENDING' | 'DISPUTED' | string;
    stateLabel?: string;
    assignmentBlock?: IRiderCashAssignmentBlock;
  };
  workIssues?: Array<IRiderControlCentreIssue>;
  trackingIssues?: Array<IRiderControlCentreIssue>;
  setupIssues?: Array<IRiderControlCentreIssue>;
  needsAttention: boolean;
  attentionReasons: Array<string>;
}

/* New rides are blocked while a rider holds too much cash (order-service FF-RDR-001). */
export interface IRiderCashAssignmentBlock {
  enabled: boolean;
  blocked: boolean;
  reasons: Array<'CASH_RIDES' | 'DISPUTED_SHORT' | string>;
  reasonText?: string | null;
  cashRides: number;
  cashRidesAmount: number;
  disputedShort: number;
  maxRides?: number | null;
  maxDisputedShort?: number | null;
  pass?: IRiderCashAssignmentPass | null;
}

/* Hub "Allow next ride": lets one blocked assignment through on the rider's current shift. */
export interface IRiderCashAssignmentPass {
  grantedBy?: number | null;
  grantedByName?: string | null;
  grantedOn?: string | null;
  reason?: string | null;
  /* What the rider was blocked for when the pass was granted; empty when granted ahead of a block. */
  blockedFor?: Array<'CASH_RIDES' | 'DISPUTED_SHORT' | string> | null;
  blockedForText?: string | null;
}

export interface IRiderControlCentreIssue {
  code: string;
  label: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | string;
  detail?: string;
}

export interface IRiderControlCentreDeviceState {
  appVersion?: string;
  trackingStatus?: RiderTrackingStatus;
  lastPingAt?: string;
  lastEventAt?: string | null;
  lastHealthAt?: string | null;
  lastLocationAt?: string | null;
  lastFacilityArrivalAt?: string | null;
  lastFacilityArrivalFacilityId?: number | null;
  lastFacilityArrivalDistanceKm?: number | null;
  lastFacilityArrivalStatus?: string | null;
  lastReceivedAt?: string;
  latitude?: number;
  longitude?: number;
  accuracyMeters?: number;
  batteryPercent?: number;
  charging?: boolean;
  foregroundServiceRunning?: boolean;
  locationPermission?: string;
  notificationPermission?: string;
  batteryOptimizationIgnored?: boolean;
  activeShiftId?: number;
  activePackageId?: number;
  activeReturnId?: number;
}

export interface IRiderControlCentreResponse {
  generatedAt: string;
  refreshAfterSeconds: number;
  summary: {
    riders: number;
    onShift: number;
    attendedToday: number;
    completedShift: number;
    notStarted: number;
    available?: number;
    onJob?: number;
    away?: number;
    assigned?: number;
    offShift?: number;
    stateUnavailable?: number;
    atFacility: number;
    inField: number;
    expectedBack: number;
    needsAttention: number;
    ridersWithWorkIssues?: number;
    ridersWithTrackingIssues?: number;
    ridersWithSetupIssues?: number;
    ridersWithCashDisputes?: number;
    ridersWithPendingCashHandover?: number;
    ridersWithCash: number;
    cashWithRiders: number;
  };
  riders: Array<IRiderControlCentreRider>;
  sectionStatus: Record<
    string,
    { status: 'AVAILABLE' | 'PARTIAL' | 'UNAVAILABLE'; message?: string }
  >;
}

export interface IRiderControlCentreEtaResponse {
  generatedAt: string;
  items: Array<{
    riderId: number;
    workType?: string;
    workId?: number;
    facilityId?: number;
    targetType?: string;
    status: 'AVAILABLE' | 'PARTIAL' | 'UNAVAILABLE';
    source?: string;
    haversineDistanceKm?: number;
    roadDistanceKm?: number;
    durationSeconds?: number;
    estimatedArrivalAt?: string;
    calculatedAt: string;
    message?: string;
  }>;
}

export interface IRiderControlCentreNavigationResponse {
  riderId: number;
  status: 'AVAILABLE' | 'UNAVAILABLE';
  targetType?: string;
  targetLabel?: string;
  facilityId?: number;
  work?: IRiderControlCentreWork;
  sourceLocationAt?: string;
  sourceLocationFreshness: 'FRESH' | 'STALE' | 'UNKNOWN';
  navigationUrl?: string;
  message?: string;
}

export interface IRiderEventHistory {
  id: number;
  eventId: string;
  eventName: string;
  riderId?: number;
  deviceId?: string;
  eventTs: string;
  receivedAt: string;
  processedAt: string;
  status: string;
  errorMessage?: string;
}

export interface IRiderCashHandoverItem {
  id: number;
  createdBy?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  version?: number;
  handoverId: number;
  packageId: number;
  orderId: number;
  salesChannelOrderId: string;
  riderIdAtCapture?: number;
  facilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  collectedAmount: number;
  packageDeliveredOn?: string;
  status: RiderCashHandoverItemStatus;
  metadata?: Record<string, unknown>;
}

export interface IRiderCashHandover {
  id: number;
  createdBy?: string;
  createdOn?: string;
  lastModifiedOn?: string;
  version?: number;
  riderId?: number;
  facilityId?: number;
  receivingFacilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  status: RiderCashHandoverStatus;
  expectedAmount: number;
  riderConfirmedAmount?: number;
  hubCountedAmount?: number;
  varianceAmount?: number;
  otpAttempts?: number;
  riderConfirmedBy?: number;
  hubConfirmedBy?: number;
  disputeResolvedBy?: number;
  cancelledBy?: number;
  riderConfirmedOn?: string;
  hubConfirmedOn?: string;
  disputeResolvedOn?: string;
  completedOn?: string;
  cancelledOn?: string;
  disputeResolvedAmount?: number;
  remarks?: string;
  disputeReason?: string;
  metadata?: Record<string, unknown>;
  items?: Array<IRiderCashHandoverItem>;
  otp?: string;
}

export interface IRiderCashHandoverCandidate {
  packageId: number;
  orderId: number;
  salesChannelOrderId: string;
  riderId?: number;
  facilityId?: number;
  collectionPartyType?: RiderCashHandoverProvider;
  collectedAmount: number;
  deliveredOn?: string;
  tryAtHome?: boolean;
  paymentMethod?: string;
  courierPartner?: string;
  courierTrackingId?: string;
  externalRiderId?: string;
  externalRiderName?: string;
  externalRiderContact?: string;
  customerName?: string;
  customerPhone?: string;
}

export enum OrderLineBrandStatus {
  // ADDED = 'ADDED',
  INTERNAL_REVIEW_DONE = 'INTERNAL_REVIEW_DONE',
  SHARED = 'SHARED',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  DISPATCHED = 'DISPATCHED',
  RECEIVED_AT_WAREHOUSE = 'RECEIVED_AT_WAREHOUSE',
  QC_FAILED = 'QC_FAILED',
  QC_PASSED = 'QC_PASSED',
}

export enum OrderLineStatus {
  CREATED = 'CREATED',
  BRAND_WIP = 'BRAND_WIP',
  FULFILLABLE = 'FULFILLABLE',
  UNFULFILLABLE = 'UNFULFILLABLE',
  WIP = 'WIP', // pushed into unicom/warehouse (after brand status = qc_pass in case of MTO+jit+mtc)
  PACKED = 'PACKED',
  DISPATCHED = 'DISPATCHED',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',

  READY_FOR_PICKUP = 'READY_FOR_PICKUP',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',

  RTO_INITIATED = 'RTO_INITIATED',
  RTO = 'RTO',

  RETURN_WIP = 'RETURN_WIP',
  RETURNED = 'RETURNED',
}

export enum DeliveryMode {
  STORE_PURCHASE = 'STORE_PURCHASE',
  STORE_PICKUP = 'STORE_PICKUP',
  HOME_DELIVERY = 'HOME_DELIVERY',
}

export enum FulfillmentMode {
  ONHAND = 'ONHAND',
  JIT = 'JIT', // MTO with inventory visibility
  MTO = 'MTO',
  MTC = 'MTC',
}

export enum VendorProgressStatus {
  PENDING_UPDATE = 'PENDING_UPDATE',
  UPDATED_ON_TRACK = 'UPDATED_ON_TRACK',
  UPDATED_WITH_REVISED_DATE = 'UPDATED_WITH_REVISED_DATE',
}

export const OrderSourceChannelSchema = z.enum(['IOS', 'ANDROID', 'WEB', 'MWEB']);

export type OrderSourceChannel = z.infer<typeof OrderSourceChannelSchema>;

export interface IIncentiveCredit {
  value?: number;
  status?: string;
  firstOrderCreditNoteId?: number;
  issuedAt?: string;
  expiresAt?: string;
  appInstallClaimExpiresAt?: string;
  appInstallVerifiedAt?: string;
  revokedAt?: string;
  revokedReason?: string;
  /**
   * Return take-back marker (line level): the line value before a non-exchange
   * return zeroed it, that return, and the Storefront revoke key. Cleared when a
   * cancelled return restores the line; `restoreOutcome` records a refused restore.
   */
  revokedValue?: number;
  revokedReturnId?: number;
  revokeReference?: string;
  restoreOutcome?: string;
  returnRevokeCycle?: number;
  policyCode?: string;
  activationGate?: string;
  rate?: number;
  cap?: number;
  entitlementLineageVersion?: number;
  entitlementRootOrderId?: number;
  entitlementRootSalesChannelOrderId?: string;
  entitlementRootAmount?: number;
  entitlementSegmentRootLineItemIds?: number[];
  entitlementSegmentAmount?: number;
  entitlementSegmentKey?: string;
  /** RELEASED: the first order kept nothing, so it closed without a credit. */
  releasedAt?: string;
  releasedReason?: string;
  /** Carry-forward: the gift moved from a released first order to an order placed meanwhile. */
  carriedForwardFromOrderId?: number;
  carriedForwardFromSalesChannelOrderId?: string;
  carriedForwardToOrderId?: number;
  carriedForwardToSalesChannelOrderId?: string;
  carryForwardCheckedAt?: string;
  carryForwardOutcome?: string;
}

/** Response-only observation, not app-download time or an eligibility decision. */
export interface ICustomerAppInstallEvidence {
  firstRecordedAt?: string | null;
  completeThrough?: string | null;
  status?: string | null;
}

export interface ICustomerOrder {
  id: number;

  billTime: string;
  createdOn: string;
  paymentOrderId: number;
  cartId: number;

  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesFacilityId: number;
  salesFacilityGroupId: number;
  salesChannelTerminalId: number;
  salesChannelOrderId: string;

  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerAppInstallEvidence?: ICustomerAppInstallEvidence | null;
  customerGSTIN?: string;
  source?: string;
  sourceChannel?: OrderSourceChannel;
  customerBillingAddressId?: number;
  customerBillingAddress?: IAddress;

  totalQuantity: number;
  totalNet: number;

  // sale staff details
  salesStaffId: number;

  salesStylistId?: number;
  // bill
  billAssetId: number;

  currency: CurrencyCode;

  // items
  lineItems: Array<IOrderLine>;

  // shipments
  packages: Array<IOrderPackage>;

  // charges
  costBreakup: ICostBreakup;

  isExchange: boolean;

  couponCode?: string;
  couponApplicationRemarks?: string;
  couponDocumentDisclosure?: {
    couponCode?: string;
  };

  firstOrderIncentiveCredit?: IIncentiveCredit;
  firstOrderCreditIncentiveActivateOn?: string;
  firstOrderIncentiveEffectiveAppInstallClaimExpiresAt?: string;
  firstOrderIncentiveQualifyingAppInstallAt?: string;
  firstOrderIncentiveEffectiveLifecycle?: IFirstOrderIncentiveEffectiveLifecycle;
}

export interface IFirstOrderIncentiveEffectiveLifecycle {
  state?: 'RTO_IN_PROGRESS' | 'NOT_AVAILABLE';
  reason?: 'RTO_INITIATED' | 'RTO' | 'RETURNED' | 'CANCELLED';
}

export interface IShelfSuggestionOption {
  shelf?: string;
  section?: string;
  inventoryType?: string;
  priority?: number;
  quantity?: number;
  quantityBlocked?: number;
  quantityNotFound?: number;
  quantityDamaged?: number;
  availableQty?: number;
}

export interface IShelfSuggestion {
  status?: string;
  reason?: string;
  primaryShelf?: string;
  facilityId?: number;
  unicomFacilityCode?: string;
  unicomFacilityName?: string;
  suggestions?: Array<IShelfSuggestionOption>;
  sourceSyncedAt?: string;
  suggestedAt?: string;
}

export interface IOrderLine {
  id: number;
  version?: number;
  courierTrackingId?: string;
  courierPartner?: CourierPartner;
  createdOn: string;
  unicomSaleOrderItemCode?: string;
  packageId: number;
  orderId: number;

  salesStaffId: number;
  salesStylistId?: number;
  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesChannelOrderId: string;
  customerId?: number;
  customerName?: string;
  customerPhone?: string;

  fulfillmentMode: FulfillmentMode;
  fulfillmentFacilityId: number;
  fulfillmentFacilityGroupId: number;
  isSameStorePurchase: boolean;
  fulfillmentFacilityType: string;
  fulfillmentFacilityAddress: IAddress;
  poReceiveFacilityId?: number;
  /** Where the piece ships from and whose invoice it is on (Fulfillment's identity check at packing). */
  shipFromFacilityId?: number;
  billFromFacilityId?: number;
  unicomFacilityCode?: string;

  skuId: number;
  skuCode: string;
  vendorSkuCode?: string;
  skuBarcode?: string;
  shelfSuggestion?: IShelfSuggestion;
  productName: string;
  productImage?: IAsset;
  color: string;
  size: string;
  hsnCode: string;
  brandId: number;
  vendorId: number;
  brandName: string;
  tryAtHome?: boolean;
  giftWrap?: boolean;

  status: OrderLineStatus;
  brandStatus?: OrderLineBrandStatus;
  cancelledOn?: string;
  vendorDeclineReason?: string;
  declineReason?: string;
  rejectionReason?: string;
  brandJitFulfillmentModel?: string;

  alterationEntry?: IAlterationDetails;

  customerDeliveryAddressId?: number;
  customerDeliveryAddress?: IAddress;
  customerDeliveryStoreId?: number;
  customerDeliveryStoreAddress?: IAddress;
  deliveryMode: DeliveryMode;
  isExpress: boolean;
  isPriority: boolean;
  opsTat: number;
  vendorDeliveryDate?: string;
  dispatchByDate?: string;
  warehouseDeliveryByDate?: string;
  vendorToWarehouseTatDays?: number;
  mtoVendorEffectiveDueDate?: string;
  vendorDeliveryDateHistory?: Array<IVendorDeliveryDateRevision>;
  effectiveBrandDispatchedOn?: string;
  isEffectivelyBrandDispatched?: boolean;
  acceptedOn?: string;
  lastVendorProgressUpdateOn?: string;
  vendorProgressStatus?: VendorProgressStatus;
  vendorProgressDisplayText?: string;
  mtoVendorProspectivePenalty?: {
    operationalEffectiveDueDate?: string;
    fullTatDueDate?: string;
    effectiveDueDate?: string;
    penaltyPolicyVersion?: string;
    penaltyBaseTatDays?: number;
    penaltyBaseTatSource?: string;
    operationalEffectiveDueGraceDays?: number;
    fullTatDueGraceDays?: number;
    penaltyGraceDays?: number;
    penaltyGraceCutoffDate?: string;
    effectiveLagDays?: number;
    graceLagDays?: number;
    prospectivePenaltyRate?: number | string;
    prospectivePenaltyAmount?: number | string;
    prospectivePenaltyCategory?: string;
  };
  mtoVendorPenalty?: {
    status?: string;
    penaltyRate?: number | string;
    penaltyAmount?: number | string;
    penaltyCategory?: string;
    effectiveLagDays?: number;
    graceLagDays?: number;
    operationalEffectiveDueDate?: string;
    fullTatDueDate?: string;
    appliedOn?: string;
    effectiveDueDate?: string;
    penaltyPolicyVersion?: string;
    penaltyBaseTatDays?: number;
    penaltyBaseTatSource?: string;
    operationalEffectiveDueGraceDays?: number;
    fullTatDueGraceDays?: number;
    penaltyGraceDays?: number;
    penaltyGraceCutoffDate?: string;
    correctionOn?: string;
    correctionReference?: string;
    correctionReason?: string;
    previousPenaltyStatus?: string;
    previousPenaltyRate?: number | string;
    previousPenaltyAmount?: number | string;
    previousEffectiveDueDate?: string;
    previousPenaltyPolicyVersion?: string;
    penaltyPoCode?: string;
    supersededByPoCode?: string;
    supersededReason?: string;
  };
  courierTat: number;
  promisedDeliveryDate: string;
  estimatedDeliveryDate: string;
  actualDeliveryDate?: string;

  returnable: boolean;
  exchangeable: boolean;
  currentlyReturnable?: boolean;
  currentlyExchangeable?: boolean;
  currentlyReturnableByAdmin?: boolean;
  currentlyExchangeableByAdmin?: boolean;
  returnableUntil?: string;
  exchangeableUntil?: string;
  cancellable?: boolean;
  cancellableByAdmin?: boolean;
  cancellationBlockedReason?: string;
  isAlterationAvailable?: boolean;

  costBreakup: ICostBreakup;
  paymentMethod: PaymentMethod;
  saleDiscountApplied?: boolean;
  history: Array<IOrderLineTrackingDetails>;

  onhold: boolean;
  onholdReason: OrderLineOnHoldReason;

  cancellationReason: string;

  returnRequestId?: number;
  returnReason?: string;
  returnSubReason?: string;
  returnEntry?: IReturnRequest;
  returnApprovalStatus?: 'APPROVAL_REQUESTED' | 'APPROVED' | 'DENIED' | null;
  returnRequestReason?: string | null;
  returnRequestApprovalSalesChannelId?: number | null;
  returnApprovalRequestedOn?: string | null;
  returnApprovalPolicy?: {
    eligible?: boolean | null;
    required?: boolean | null;
    reasons?: string[] | null;
    warning?: string | null;
  } | null;

  refundRequestId?: number;
  refunded?: boolean;
  refundRequest?: IRefundRequest;
  reshipRequest?: IReshipRequest;

  unicomPurchaseOrderCode: string;
  purchaseOrderCreatedOn: string;
  purchaseOrderPdfAsset: IAsset;

  productId: number;
  skuEntry?: ISKU;

  exchangedForLineItemId?: number;
  isExchange: boolean;
  giftVoucherCode?: string;

  giftVoucherAsset?: {
    pngAsset?: {
      createdBy: string;
      createdOn: string;
      lastModifiedOn: string;
      version: number;
      id: number;
      type: string;
      bucket: string;
      path: string;
      usage: string;
      source: string;
      url: string;
      cleanUrl: string;
      uploaded: boolean;
      ready: boolean;
    };
  };

  cancellationSalesStaffId?: number;
  taggedForFrequentReturn?: boolean;
  refundPolicyMetadata?: Record<string, unknown>;
  mtocMetadata?: Record<string, unknown>;
  firstOrderIncentiveCredit?: IIncentiveCredit;
}

export interface IOrderLineTrackingDetails {
  brandStatus: OrderLineBrandStatus;
  remark: string;
  eventDate: string; // We'll use string for ZonedDateTime
  icon: string;
  status?: OrderLineStatus;
  actorId?: number;
}

export interface IVendorDeliveryDateRevision {
  previousVendorDeliveryDate?: string;
  revisedVendorDeliveryDate: string;
  revisedAt: string;
  reason?: string;
  source:
    | 'INITIAL_STAMP'
    | 'BRAND_ACCEPTED'
    | 'BRAND_STATUS_UPDATE'
    | 'ADMIN_MANUAL_UPDATE'
    | 'PACKAGE_EDIT'
    | 'SYSTEM_RECOMPUTE'
    | 'AUDIT_BACKFILL';
  actorId?: number;
}

export interface IAlterationDetails {
  referenceColor: string;
  referenceSize: string;
  remarks: string;
  customizationCategory: CustomizationCategory;
  topwearAlterationEntry?: ITopwearAlterationDetails;
  bottomwearAlterationEntry?: IBottomwearAlterationDetails;
}

export interface ITopwearAlterationDetails {
  shoulder: string;
  bust: string;
  armHole: string;
  waist: string;
  hips: string;
  sleeveLength: string;
  sleeveRound: string;
  dressLength: string;
}

export interface IBottomwearAlterationDetails {
  waist: string;
  hips: string;
  thigh: string;
  pantLength: string;
}

export enum CustomizationCategory {
  DRESS = 'DRESS',
  TOP_WEAR = 'TOP_WEAR',
  BOTTOM_WEAR = 'BOTTOM_WEAR',
  CO_ORD_SET = 'CO_ORD_SET',
}

export interface IOfferDiscountEntry {
  bundleId: number;
  name: string;
}

export interface ICostBreakup {
  mrp: number;
  tradeDiscount: number;
  sellingPrice: number;
  taxRate: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxValue: number;
  taxableValue: number;
  couponCode?: string;
  couponDiscount?: number;
  bagDiscount?: number;
  offerDiscount?: number;
  offerDiscountEntry?: IOfferDiscountEntry;
  shippingCharge?: number;
  tahServiceFeeAmount?: number;
  codCharge?: number;
  customCharges?: Array<ICustomCharge>;
  customChargesViaOrder?: Array<ICustomCharge>;
  internationalDuty: number;
  internationalTax: number;
  chargeableAmount: number;

  amountPaid: number;
  amountRemaining: number;
  amountPaidBeforeDelivery: number;
  amountPaidOnDelivery: number;

  refundAmount: number;

  currency?: CurrencyCode;
  amountPaidUsingCredits?: number;
}

export interface IRefundRequest {
  id?: number;
  action?: RefundTriggerAction | string;
  orderId?: number;
  salesChannelOrderId?: string;
  refundMode: RefundMode;
  refundRemarks?: string;
  refundAmount: number;
  originalRefundAmount?: number;
  refundAmountAdjustmentDone?: boolean;
  refundAmountAdjustmentReason?: string;
  refundAmountAdjustedOn?: string;
  upiHandle?: string;
  storeCreditNumber?: string;
  bankAccount?: IBankAccount;
  status: RefundRequestStatus;
  payoutTxnId?: string;
  payoutMetadata?: Record<string, string | null | undefined>;
  refundDisplay?: IRefundDisplay;
  refundLifecycle?: IRefundLifecycle;
  retryPolicy?: {
    allowedModes: Array<RefundMode>;
    modeChangeAllowed?: boolean;
    restrictionReason?: string;
  };
  onhold: boolean;
  onholdReason: string;
  onholdActionHistory?: IRefundOnholdActionHistoryEntry[];
  onholdShelved?: boolean;
  onholdShelfNote?: string;
  onholdShelvedOn?: string;
  orderLineItemIds: Array<number>;
  createdOn?: string;
  createdBy?: string;
  lastModifiedOn?: string;
  lastModifiedBy?: string;
  /** When the refund reached a final payout result (paid or failed). For RTO refunds it is the creation time. */
  refundedOn?: string;
  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  isTestOrder?: boolean;
  returnId?: number;
  /** Present only when all linked refund lines currently resolve to one package. */
  resolvedPackageId?: number;
}

export interface IRefundOnholdActionHistoryEntry {
  action?: string;
  comment?: string;
  actorId?: number;
  actorName?: string;
  actedOn?: string;
  amountBefore?: number;
  amountAfter?: number;
}

export interface IRefundLifecycle {
  stage:
    | 'AWAITING_PICKUP'
    | 'AWAITING_FACILITY_RECEIPT_AND_PUTAWAY'
    | 'AWAITING_REFUND_REVIEW'
    | 'AWAITING_PROCESSING'
    | 'INITIATED'
    | 'COMPLETED'
    | 'FAILED'
    | 'DISCARDED'
    | 'RTO_IN_PROGRESS';
  releaseTrigger?:
    | 'PICKUP_CONFIRMED'
    | 'FACILITY_RECEIPT_AND_PUTAWAY'
    | 'RETURN_ACCEPTANCE'
    | 'RTO_CONFIRMATION'
    | 'NOT_APPLICABLE';
  title: string;
  description: string;
}

export interface IRefundDisplay {
  split?: boolean;
  title?: string;
  description?: string;
  totalAmount?: number;
  components?: IRefundDisplayComponent[];
  retainedReasons?: string[];
  adjustments?: IRefundAdjustment[];
}

export interface IRefundDisplayComponent {
  refundMode?: RefundMode;
  label?: string;
  amount?: number;
  status?: string;
  reference?: string;
}

export interface IRefundOptionQuote {
  eligible?: boolean;
  ineligibleReason?: string;
  action?: RefundTriggerAction | string;
  orderId?: number;
  salesChannelOrderId?: string;
  orderLineItemIds?: number[];
  refundAmount?: number;
  cashEquivalentRefundAmount?: number;
  sourceRefundAmount?: number;
  retainedStoreCreditAmount?: number;
  defaultRefundMode?: RefundMode;
  options?: IRefundOption[];
  warnings?: string[];
  adjustments?: IRefundAdjustment[];
  /** Staff in-store (AT_STORE) return quotes only: the gift is applied to an unfinished POS cart. */
  firstOrderIncentiveHold?: IFirstOrderIncentiveHold | null;
}

export interface IFirstOrderIncentiveHold {
  creditNoteId?: number;
  creditNoteReferenceId?: string;
  takeBackAmount?: number;
  heldAmount?: number;
  cartId?: string;
  cartSalesChannelOrderId?: string;
  posTerminalId?: number;
  heldSince?: string;
}

export interface IRefundAdjustment {
  code?: string;
  type?: 'DEDUCTION' | string;
  label?: string;
  amount?: number;
  message?: string;
}

export interface IRefundOption {
  refundMode: RefundMode;
  label?: string;
  description?: string;
  enabled?: boolean;
  disabledReason?: string;
  primaryAmount?: number;
  retainedStoreCreditAmount?: number;
  split?: boolean;
  requiresBankAccount?: boolean;
  requiresUpiHandle?: boolean;
  components?: IRefundOptionComponent[];
  retainedReasons?: string[];
}

export interface IRefundOptionComponent {
  refundMode?: RefundMode;
  label?: string;
  amount?: number;
}

export interface IFrequentReturnRefundRelaxationQuoteLine {
  orderLineItemId: number;
  packageId?: number;
  skuCode?: string;
  productName?: string;
  taggedForFrequentReturn?: boolean;
  relaxed?: boolean;
  relaxedBy?: number;
  relaxedOn?: string;
  relaxationReason?: string;
  refundRequestId?: number;
  tahServiceFeeAmount?: number;
  action?: string;
}

export interface IFrequentReturnRefundRelaxationQuoteRefund {
  refundRequestId: number;
  status?: RefundRequestStatus;
  refundMode?: RefundMode;
  currentRefundAmount?: number;
  originalRefundAmount?: number;
  pendingAmount?: number;
  refundAmountAfterException?: number;
  action?: string;
  blockedReason?: string;
  alreadyRelaxed?: boolean;
  topUpAlreadyRequested?: boolean;
  topUpPayoutId?: string;
  topUpStatus?: string;
  orderLineItemIds?: Array<number>;
}

export interface IFrequentReturnRefundRelaxationQuote {
  orderId?: number;
  refundRequestId?: number;
  salesChannelOrderId?: string;
  eligible?: boolean;
  ineligibleReason?: string;
  totalPendingAmount?: number;
  lineItems?: Array<IFrequentReturnRefundRelaxationQuoteLine>;
  refunds?: Array<IFrequentReturnRefundRelaxationQuoteRefund>;
}

export interface IFrequentReturnRefundRelaxationResultOutcome {
  refundRequestId?: number;
  refundMode?: RefundMode;
  amount?: number;
  outcome?: string;
  requiresRetry?: boolean;
  payoutId?: string;
  payoutStatus?: string;
  storeCreditNumber?: string;
}

export interface IFrequentReturnRefundRelaxationResult {
  orderId?: number;
  refundRequestId?: number;
  totalPendingAmount?: number;
  relaxedOrderLineItemIds?: Array<number>;
  refundOutcomes?: Array<IFrequentReturnRefundRelaxationResultOutcome>;
}

export interface IMtoMtcCodRtoAdvanceWaiverQuote {
  refundRequestId?: number;
  orderId?: number;
  salesChannelOrderId?: string;
  packageId?: number;
  packageVersion?: number;
  orderLineItemId?: number;
  orderLineItemVersion?: number;
  refundVersion?: number;
  packageStatus?: string;
  courierPartner?: string;
  awb?: string;
  rtoRefundAuthorityType?: string;
  eligible?: boolean;
  alreadyApplied?: boolean;
  ineligibleReason?: string;
  ordinaryPolicyRefundAmount?: number;
  retainedAdvanceAmount?: number;
  currentRefundAmount?: number;
  additionalComponentAmount?: number;
  finalRefundAmount?: number;
  executionMode?: 'ZERO_VALUE_REOPEN' | 'PROCESSED_PARTIAL_TOPUP' | string;
  refundMode?: RefundMode;
  sourceAmount?: number;
  storeCreditAmount?: number;
  topUpPayoutId?: string;
  topUpPayoutStatus?: string;
  topUpStoreCreditNumber?: string;
}

export interface IMtoMtcCodRtoAdvanceWaiverResult {
  refundRequestId?: number;
  refundMode?: RefundMode;
  amount?: number;
  outcome?: string;
  refundInitiationRequested?: boolean;
  topUpPayoutId?: string;
  topUpPayoutStatus?: string;
  topUpStoreCreditNumber?: string;
}

export interface IBankAccount {
  accountNumber: string;
  accountName: string;
  ifsc: string;
  bankName: string;
}

export interface IDelayedExpressShippingRefundQuoteLine {
  orderLineItemId: number;
  packageId?: number;
  skuCode?: string;
  productName?: string;
  promisedDeliveryDate?: string;
  actualDeliveryDate?: string;
  shippingCharge: number;
  alreadyCompensatedShippingCharge: number;
  refundableShippingCharge: number;
  existingAdjustmentShippingCharge?: number;
  remainingRefundableShippingCharge?: number;
}

export interface IDelayedExpressShippingRefundQuote {
  orderId: number;
  salesChannelOrderId?: string;
  totalDelayedShippingCharge: number;
  alreadyCompensatedShippingCharge: number;
  existingAdjustmentAmount: number;
  refundableAmount: number;
  existingRefundRequestId?: number;
  existingRefundStatus?: RefundRequestStatus;
  sourceRefundEligible?: boolean;
  sourceRefundIneligibilityReason?: string;
  lineItems: Array<IDelayedExpressShippingRefundQuoteLine>;
}

export enum ChargeType {
  SHIPPING = 'SHIPPING',
  BRAND_CUSTOMIZATION = 'BRAND_CUSTOMIZATION',
  ALTERATION = 'ALTERATION',
  GIFT_WRAP = 'GIFT_WRAP',
}

export interface ICustomCharge {
  chrargeDirection: string;
  amount: number;
  remarks: string;
  type: ChargeType;
}

export type PickingInstructionSource = 'RESERVED' | 'SUGGESTED';

/** One unit of a server picking session: where to pick it from. */
export interface IPackagePickingSessionUnit {
  orderLineItemId: number;
  itemCode?: string;
  skuCode?: string;
  instructionSource: PickingInstructionSource;
  shelfCode?: string;
  suggestedShelves?: string[];
  /** Shelves already reported for this unit; suggestions exclude them. */
  triedShelves?: string[];
  attempts?: IPickingShelfAttempt[];
  /** Set once nothing more can be tried at this facility (picking design §5). */
  nextAction?: PickingNextAction;
  /**
   * SHELF_EMPTY: "Shelf empty?" after a not-found answer that still shows units on the shelf.
   * RELEASE_NOT_CONFIRMED: Unicom has not confirmed it let go of the unit; nothing was corrected.
   */
  prompt?: 'SHELF_EMPTY' | 'RELEASE_NOT_CONFIRMED' | 'CONFIRM_NOT_FOUND';
  promptShelf?: string;
  promptQuantity?: number;
  /** The report the current prompt answers (the final Not found confirmation sends it back). */
  promptAttemptId?: string;
  /** Where the current round of attempts starts in `attempts` (null = 0). */
  roundStartAttempt?: number;
  /**
   * Shelf-by-shelf Not found (4 Nov 2026): the end held for the one final "Not found anywhere in this facility?"
   * (`prompt = CONFIRM_NOT_FOUND`), then who confirmed it, when, and the shelves it named.
   */
  pendingNextAction?: PickingNextAction;
  absenceConfirmedOn?: string;
  absenceConfirmedByName?: string;
  absenceCheckedShelves?: string[];
  /** Freshness of the shelf-stock snapshot behind `suggestedShelves` (ISO instant), when known. */
  suggestionAsOf?: string;
  /** Shelf the healthy pick came from ("Picked from"), set when Confirm Picking records it. */
  pickedShelf?: string;
  /** Server-resolved physical destination for a QC problem; never inferred from the origin. */
  qualityDestinationShelfCode?: string;
}

/**
 * Stamped on the session the first time picking touches the package and never changed afterwards.
 * UNICOM: Unicom reserves a unit per shelf. LOCAL: shelf suggestions only, no Unicom writes.
 */
export type PickingMode = 'UNICOM' | 'LOCAL';

export type PickingAttemptReason = 'NOT_FOUND' | 'DAMAGED' | 'REPAIRABLE';
export type PickingNextAction = 'NO_UNIT_LEFT' | 'ATTEMPT_LIMIT';
export type PickingStockWriteOutcome =
  | 'NOT_SENT'
  | 'DISPATCHING'
  | 'APPLIED'
  | 'REFUSED'
  | 'UNKNOWN'
  /** An abandoned attempt's unsent follow-up: like NOT_SENT, never an unconfirmed correction. */
  | 'SKIPPED';

/** One Unicom stock call of a shelf attempt; a write that may have applied is never re-sent. */
export interface IPickingStockWrite {
  call?: string;
  shelfCode?: string;
  quantity?: number;
  outcome?: PickingStockWriteOutcome;
  errorCodes?: string[];
  remaining?: number;
  at?: string;
}

/** One confirmed observation of one unit at one shelf. */
export interface IPickingShelfAttempt {
  attemptId: string;
  collectionId?: string;
  qualityReason?: string;
  photoAssets?: IAsset[];
  shelfCode?: string;
  reason?: PickingAttemptReason;
  createdOn?: string;
  writes?: IPickingStockWrite[];
}

/** One acknowledged physical collection; replacement of the same line gets a new collectionId. */
export interface IPickingCollection {
  orderLineItemId: number;
  collectionId: string;
  scannedBarcode: string;
  scannedAt?: string;
  shelfCode?: string;
  needsChecking?: boolean;
  collectedByName?: string;
  collectedByUserId?: string;
  collectedOn?: string;
  pickedShelfSource?: string;
}

export type PickingQualityCondition = 'GOOD' | 'DAMAGED' | 'REPAIRABLE' | 'NEEDS_ATTENTION';
export interface IPickingQualityCheck {
  orderLineItemId: number;
  collectionId: string;
  condition: PickingQualityCondition;
  state?: 'GOOD' | 'PLACEMENT_REQUIRED' | 'REPORTED' | 'UNRESOLVED';
  recordedOn?: string;
  recordedByUserId?: string;
  reportId?: string;
  reason?: 'WRONG_ITEM' | 'WRONG_SIZE' | 'MISSING_FROM_TRAY' | 'INCOMPLETE_SET';
  /** Client request IDs; omitted for GOOD and unchanged historical no-photo continuations. */
  photoAssetIds?: number[];
  /** Server-resolved uploaded ORDER images for this exact physical collection. */
  photoAssets?: IAsset[];
  physicalPieceFoundConfirmed?: boolean;
  scannedBarcode?: string;
  destinationShelfCode?: string;
  physicalPlacementConfirmed?: boolean;
  inspectionConfirmed?: boolean;
  readyBy?: string;
  etaUnknown?: boolean;
}

/** Exact server-accepted QC command. Recovery sends it unchanged, including original physical proof. */
export interface IPickingQualityRequest {
  sessionId: string;
  qcRequestId: string;
  collectionSetKey: string;
  outcomes: IPickingQualityCheck[];
}

/** `PUT /order/package/{id}/picking/start`: one session per package, shared by every device. */
export interface IPackagePickingSession {
  sessionId: string;
  /** Frozen per picking generation. Absence is a legacy session, never inferred QC approval. */
  workflowContract?: 'PICK_QC_PACK_V1';
  workflowPhase?: 'PICKING' | 'QC' | 'PACKING';
  collectionSetKey?: string;
  collections?: IPickingCollection[];
  qualityChecks?: IPickingQualityCheck[];
  qualityConfirmedCollectionSetKey?: string;
  qualityRequestId?: string;
  qualityResultStatus?: string;
  qualityRequestedCollectionSetKey?: string;
  qualityAcceptedRequest?: IPickingQualityRequest | null;
  /** Absent on sessions started before the mode was stamped: they render as before. */
  pickingMode?: PickingMode;
  /**
   * LOCAL: Start Picking on a package whose case waits at the source (NO_OPTION, AWAITING_CUSTOMER, or
   * AWAITING_REPAIR without `sourceItemReady`). The case's unit stays stopped and Confirm is refused for it until
   * `PUT …/picking/item-back` revives it, so the screen opens the "Item is back" sheet for that unit.
   */
  waitingItemBack?: boolean;
  startedOn?: string;
  startedByUserId?: string;
  startedByName?: string;
  lastStartedOn?: string;
  units?: IPackagePickingSessionUnit[];
  /** Raised by each packing correction (`PUT …/picking/correction`). */
  packingAttempt?: number;
  lastCorrection?: IPickingCorrection;
  /** Set when a payment after Confirm Picking rebuilt the invoice (COD to PREPAID); reprint it. */
  invoiceReprintRequiredOn?: string;
  /** The planned route is not booked automatically (SELF, SELF_EXPRESS...): assign a rider or courier first. */
  courierAssignmentRequiredFor?: string | null;
  /** A manually assigned courier's label, printed and scanned before the packing video. */
  manualCourierLabel?: IPickingManualCourierLabel | null;
}

export interface IPickingManualCourierLabel {
  labelPurpose?: 'LOCAL_PARCEL' | 'PROVIDER_SHIPPING';
  contentIdentity?: string;
  courierPartner?: string;
  awb?: string;
  riderId?: number | null;
  labelUrl?: string;
  labelBarcode?: string;
  preparedOn?: string;
}

/** The last packing correction: picking reopened and any parked label was voided. */
export interface IPickingCorrection {
  reason?: string;
  correctedOn?: string;
  correctedByName?: string;
  voidedOperationId?: string;
  /** AWB of the label the correction voided. */
  voidedAwb?: string;
}

export interface IPackagePickingAllocation {
  checkedOn?: string;
  trigger?: string;
  saleOrderCode?: string;
  unicomFacilityCode?: string;
  units?: Array<{
    orderLineItemId?: number;
    itemCode?: string;
    skuCode?: string;
    state?: string;
    shelfCode?: string;
    unicomStatus?: string;
    lastAllocateOn?: string;
  }>;
}

export type CustomerCallOutcome = 'NO_ANSWER' | 'CALL_BACK_LATER' | 'WRONG_NUMBER' | 'OTHER';

/** A Customer Care call on a parcel (Fulfillment CustomerCallEntry, `CUSTOMER_CALL_LOGGED`). */
export interface ICustomerCallEntry {
  eventId?: number;
  packageId?: number;
  outcome?: CustomerCallOutcome | string;
  outcomeLabel?: string;
  note?: string;
  calledAt?: string;
  calledById?: number;
  calledByName?: string;
  /** Set on the call-outcome response when an identical call was already logged (2-minute window). */
  duplicate?: boolean;
}

/**
 * Read-only facts of a Needs-a-call row (Fulfillment DeliveryCallQueue, 27 Oct). The server omits null
 * fields, so every field is optional. The latest attempt failed and no time was requested since it.
 */
export interface IDeliveryCallQueue {
  attemptCount?: number;
  lastAttemptAt?: string;
  lastAttemptReason?: string;
  lastAttemptSource?: 'RIDER' | 'COURIER' | string;
  lastAttemptRiderId?: number;
  lastAttemptRiderName?: string;
  lastAttemptCourierPartner?: string;
  lastAttemptAwb?: string;
  customerName?: string;
  customerPhone?: string;
  requestedDeliveryDate?: string;
  requestedDeliveryTime?: string;
  /** End of the requested slot (HH:mm) when the request is a window ("14:00-16:00"). */
  requestedDeliveryTimeEnd?: string;
  requestedSetAt?: string;
  /** Customer Care's latest call since the latest failed attempt. */
  lastCall?: ICustomerCallEntry | null;
  callsSinceLastAttempt?: number;
  estimatedDeliveryDate?: string;
  promisedDeliveryDate?: string;
  facilityId?: number;
  facilityName?: string;
  /** True on every row when more parcels need a check than the queue reads (it keeps the newest). */
  listIncomplete?: boolean;
}

export interface IOrderPackage {
  /** Comma-separated Unicommerce-mandatory fields missing while held with UNICOM_DATA_VALIDATION_ERR. */
  unicomDataValidationErrors?: string | null;
  /** Backend-derived local-route capability; absent/false means no rider override for store transfers. */
  localStoreTransferRiderAllowed?: boolean;
  id: number;
  version?: number;

  orderId: number;

  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerGSTIN?: string;
  customerBillingAddressId?: number;
  customerBillingAddress?: IAddress;

  salesChannelOrderId: string;
  salesChannelId: number;
  salesChannelType: SalesChannelType;
  salesFacilityId: number;
  salesFacilityGroupId: number;
  billFromFacilityId?: number;
  /** Unset on older packages: Fulfillment then reads the fulfillment facility. */
  shipFromFacilityId?: number;
  salesStaffId: number;

  currentStatus: PackageStatus;
  refundLifecycle?: IRefundLifecycle;
  history: Array<IPackageTrackingDetails>;
  orderLineItemIds: Array<number>;
  orderLineItems?: Array<IOrderLine>;

  fulfillmentFacilityId: number;
  fulfillmentMode: FulfillmentMode;
  /** Server-derived current pickup; original fulfillment source remains unchanged. */
  effectivePickupFacilityId?: number;
  forwardShippingAuthority?: 'UNICOM_LEGACY' | 'LYSKRAFT_V1';
  fulfillmentFacilityGroupId: number;
  fulfillmentFacilityType?: string;
  giftWrap?: boolean;
  brandJitFulfillmentModel?: string;
  unicomFacilityCode?: string;

  customerDeliveryAddress?: IAddress;
  customerDeliveryStoreId?: number;
  customerDeliveryStoreAddress?: IAddress;
  courierTrackingId?: string;
  /** A courier RTO's own return AWB, when the courier gave one. */
  rtoTrackingNumber?: string;
  courierShipmentId?: string;
  courierOrderId?: string;
  shippingLabelUrl?: string;
  courierPickupRequested?: boolean;
  courierPartner?: CourierPartner;
  courierPartnerName?: string;
  courierUpdated?: boolean;
  riderId?: number;
  riderAssigned?: boolean;
  facilityHandover?: {
    required?: boolean;
    state?: string;
    source?: string;
    /** Fulfillment sets this only while a verified Hub receipt is the parcel's current custody. */
    currentCustodyFacilityId?: number | null;
    vendorState?: string;
    /** The seller trip, as on the handover status: lets the page decide before that status loads. */
    sellerLeg?: ISellerLeg | null;
  };
  /** Fulfillment's verdict: the confirming rider still holds this doorstep-cancelled parcel. */
  riderCancellationHeld?: boolean;
  /**
   * Fulfillment's verdict on how the parcel comes back to a facility: CODE (rider handover by code applies),
   * MANUAL_RETURN (undelivered with a SELF/SELF_EXPRESS carrier: "Mark returned to facility") or NONE.
   */
  handoverMode?: HandoverMode;
  /** Customer Care's Needs-a-call facts: only on `delivery-follow-ups/search` NEEDS_CALL rows. */
  deliveryCallQueue?: IDeliveryCallQueue | null;
  /** Fulfillment's verdict: the courier's latest update on the current AWB is a failed delivery attempt. */
  courierDeliveryFailure?: {
    courierPartner?: string;
    reasonCode?: string | null;
    reason?: string | null;
    attempt?: number | null;
    failedOn?: string | null;
  } | null;
  /** Fulfillment's audit: when the package entered its current status (list and package page only). */
  currentStatusSince?: string | null;
  deliveryMode: DeliveryMode;
  storePickupTransferEligible?: boolean;
  isExpress: boolean;
  isPriority: boolean;
  opsTat: number;
  vendorDeliveryDate?: string;
  dispatchByDate?: string;
  warehouseDeliveryByDate?: string;
  /** JIT B2B2C RTO waiting for brand receipt: when Fulfillment auto-confirms it (may be past until the job runs). */
  brandReceiptAutoConfirmOn?: string | null;
  /** Set only when Fulfillment auto-confirmed brand receipt (no brand confirmation within 7 days). */
  brandReceiptAutoConfirmedOn?: string | null;
  vendorToWarehouseTatDays?: number;
  courierTat: number;
  promisedDeliveryDate: string;
  estimatedDeliveryDate: string;
  actualDeliveryDate?: string;

  unicomSaleOrderCode?: string;
  unicomShippingPackageCode?: string;
  invoiceNumber?: string;
  invoiceAsset?: IAsset;
  labelAsset?: IAsset;
  lkGeneratedShippingLabelUrl?: string;

  shippingManifestUrl?: string;

  paymentMethod: PaymentMethod;
  costBreakup: ICostBreakup;

  createdOn: string;
  packedOn?: string;
  invoicedOn?: string;
  cancellationReason: string;
  rtoReason: string;

  courierMetadata: Record<string, string>;
  packingMetadata?: {
    status?: string;
    /** The package's stamped picking mode (read first; `pickingSession.pickingMode` is the fallback). */
    pickingMode?: PickingMode;
    sessionId?: string;
    source?: string;
    packedOn?: string;
    packedByUserId?: string;
    packedByName?: string;
    orderLineItemIds?: number[];
    items?: Array<{
      orderLineItemId?: number;
      skuCode?: string;
      matchedBy?: string;
      scannedAt?: string;
    }>;
    auditAssets?: Array<{
      asset?: IAsset;
      uploadedOn?: string;
      uploadedByUserId?: string;
      uploadedByName?: string;
      source?: string;
      retainedFromPreviousSession?: boolean;
    }>;
    auditUpdatedOn?: string;
    auditUpdatedByUserId?: string;
    auditUpdatedByName?: string;
    activePickingException?: {
      status?: string;
      taskId?: number;
      sessionId?: string;
      orderLineItemId?: number;
      reason?: 'NOT_FOUND' | 'DAMAGED' | 'REPAIRABLE';
      phase?: string;
      sourceFacilityId?: number;
      reportedOn?: string;
      /** Repair at the source: Unicom reserves a good unit again, so the store can pack it. */
      sourceItemReady?: boolean;
      /** The case's plan: `FOUND_AT_SOURCE` for a unit found here, a `REPAIR_*` plan for a repair. */
      planDecision?: string;
    };
    /** A unit moved out at Confirm Picking: its hand-off to stock or to Customer Care. */
    pendingUnpickable?: {
      orderLineItemId?: number;
      reason?: 'NOT_FOUND' | 'DAMAGED' | 'REPAIRABLE';
      status?: 'PENDING' | 'CASE_OPENED' | 'RESERVED_AGAIN' | 'NEEDS_ATTENTION';
      note?: string;
    };
    /** Unicom allocation per unit; present before picking starts, so not a "started" signal. */
    pickingAllocation?: IPackagePickingAllocation;
    /** Server-issued picking session (Start Picking); not a "started/packed" signal by itself. */
    pickingSession?: IPackagePickingSession;
  };
  riderActionAddress?: Record<string, unknown>;
  deliveryOtpRequired?: boolean;
  deliveryOtpSent?: boolean;
  /** Server-derived approval for the current rider visit; missing means no exception. */
  deliveryScanExceptionApproved?: boolean;
  /** Server-derived: support approved this rider leg's location-bound action (rider action geofence). */
  geofenceExceptionApproved?: boolean;
  /** Server-derived: the rider step a location exception would cover now (ARRIVAL or PACKAGE_DROP); null when none. */
  geofenceStep?: 'ARRIVAL' | 'PACKAGE_DROP' | null;
  /** Server-derived: a doorstep scan exception can be decided for the current visit (DeliveryScanExceptionPolicy). */
  deliveryScanExceptionEligible?: boolean;
  /** The delivery address coordinate is not the customer's own pin; null when there is none. */
  destinationCoordinatesEstimated?: boolean | null;
  trackingUrl: string;

  billTo?: {
    name: string;
    address: IAddress;
    phone?: string;
  };
  shipTo?: {
    name: string;
    address: IAddress;
    phone?: string;
  };
  sellerInfo?: {
    logo?: IAsset;
    name?: string;
    address?: IAddress;
    phone?: string;
    email?: string;
    gstin?: string;
    gstLut?: string;
    pan?: string;
    cin?: string;
  };

  recreatePackage?: boolean;
  vendorIds?: Array<number>;
  jitVendorId?: number;
  isTestOrder?: boolean;
  isInternational?: boolean;

  expressServiceabilityBucket?: '60m' | '90m' | '120m' | '150m' | 'beyond';
  crnBelongsToAnotherPackageAsWell?: boolean;
  tryAtHome?: boolean;
  stockTransferId?: number;
  stockTransferRequestNumber?: string;
}

/** Today v2 views (Fulfillment TodayViewEntry, 29 Oct): three action tabs, then six watching counts. */
export type TodayViewKey =
  | 'to_pick'
  | 'to_hand_over'
  | 'to_receive'
  | 'waiting_vendor'
  | 'waiting_case'
  | 'waiting_customer'
  | 'vendor_handles'
  | 'out'
  | 'at_store';

/** To hand over rows: how the parcel leaves. */
export type TodayCarrier = 'OWN_FLEET' | 'COURIER' | 'STORE_TRANSFER' | 'SELLER';

/** To receive rows: what is coming back to the expected facility. */
export type TodayReceiveKind = 'RIDER_BACK' | 'RTO' | 'TRANSFER_IN' | 'SELLER_BACK';

/** One tab or watching count; counts are over every open package in scope, never capped. */
export interface ITodayViewCount {
  key: TodayViewKey;
  kind: 'TAB' | 'WATCH';
  label: string;
  count: number;
  /** Rows in this view the server flagged (attention reason, or overdue/waiting > 2 days). */
  flagged: number;
}

/** `POST order/package/today/view` (DataResponse<TodayViewEntry>). */
export interface ITodayView {
  view: TodayViewKey;
  /** All nine keys, in display order. */
  views: Array<ITodayViewCount>;
  header: {
    packedOver48h: number;
    oldestToReceiveSince?: string | null;
  };
  /** The requested view only, sorted by the server. */
  rows: Array<ITodayWorkRow>;
  /** True only when the safety cap bound the classification. */
  truncated: boolean;
  generatedAt: string;
}

/**
 * Fulfillment's Hub Today row (TodayWorkEntry): view, next step, due-by, flag and attention are decided on the
 * server (TodayRules); the page only renders, filters and searches.
 */
export interface ITodayWorkRow {
  id: number;
  /** Only ever true on Stage/local, where TEST orders may be listed. */
  isTestOrder?: boolean;
  orderId?: number;
  salesChannelOrderId?: string;
  customerName?: string;
  courierTrackingId?: string;
  currentStatus: PackageStatus;
  deliveryMode?: DeliveryMode;
  fulfillmentMode?: FulfillmentMode;
  courierPartner?: CourierPartner;
  isExpress?: boolean;
  isPriority?: boolean | null;
  tryAtHome?: boolean;
  /** Not in the v2 contract's kept list; read when present (Vendor ships tag). */
  vendorOwned?: boolean | null;
  view: TodayViewKey;
  nextStep: string;
  /** An attention reason applies, or the work is overdue/waiting more than 2 days. Nothing folds. */
  flagged?: boolean;
  flagReason?: string | null;
  /** ISO instant the flag's clock started: the card shows the age from it. */
  flagSince?: string | null;
  /** to_hand_over only. */
  carrier?: TodayCarrier | null;
  /** to_hand_over only: a failed delivery going out again. */
  reattempt?: boolean | null;
  /** to_receive only. */
  receiveKind?: TodayReceiveKind | null;
  /** to_receive only: where the parcel is expected (scope is by expected destination). */
  expectedFacilityId?: number | null;
  expectedFacilityName?: string | null;
  holdingFacilityName?: string | null;
  due: {
    at?: string | null;
    date?: string | null;
    time?: string | null;
    /** End of the customer's slot (HH:mm IST) when the request is a window; due until then. */
    endTime?: string | null;
    source: string;
    bucket: string;
    daysLate: number;
    since?: string | null;
  };
  attention?: { key: string; label: string; step: string } | null;
  /** Readable reason of the latest failed delivery attempt (Fulfillment TodayWorkEntry). */
  lastFailureReason?: string | null;
  /** ISO instant of that failure: the rider's captured time when present. */
  lastFailureAt?: string | null;
  /** Where the parcel is: "With rider {name}", "At {facility}", "With the courier", "Custody not confirmed". */
  custody?: string | null;
  /**
   * Why a failed delivery is where it is (Fulfillment TodayWorkEntry): AWAITING_CC_CALL is Customer Care's
   * Needs-a-call queue (latest attempt failed, no requested time since; the waiting_customer view).
   * Read through `followUpFacts` (today/TodayCards.tsx) only.
   */
  followUpState?: 'AWAITING_CC_CALL' | 'RETURNING' | 'CANCELLED_AT_DOOR' | null;
  /** AWAITING_CC_CALL rows: failed attempts so far and whose the latest was. */
  attemptCount?: number | null;
  lastAttemptSource?: 'RIDER' | 'COURIER' | null;
  /** AWAITING_CC_CALL rows: Customer Care's latest call since that attempt, if any. */
  lastCallOutcome?: string | null;
  lastCallOutcomeLabel?: string | null;
  lastCallAt?: string | null;
  lastCallByName?: string | null;
  item?: {
    productName?: string | null;
    imageUrl?: string | null;
    size?: string | null;
    color?: string | null;
    count: number;
  } | null;
}

export interface IPackageTrackingDetails {
  status: PackageStatus;
  remark: string;
  eventDate: string; // We'll use string for ZonedDateTime
  icon: string;
  actorId?: number;
}

export interface IPackageOperationalTimelineEvent {
  contextLabel?: string;
  deliveryAttemptKey?: string;
  deliveryAttemptNumber?: number;
  deliveryAttemptLabel?: string;
  key: string;
  orderLineItemId?: number;
  // Nullable on the backend entry: keep these optional so the UI is written to handle a sparse
  // event rather than trusting a contract the API does not actually guarantee.
  phase?: string;
  eventCode?: string;
  title?: string;
  description?: string;
  occurredOn?: string;
  recordedOn?: string;
  actorType?: string;
  actorId?: number;
  actorRef?: string;
  actorName?: string;
  portalCode?: string;
  app?: string;
  source?: string;
  sourceReference?: string;
  attemptGroupId?: string;
  courierPartner?: string;
  awb?: string;
  providerOrderId?: string;
  providerShipmentId?: string;
  reason?: string;
  // The backend sends a plain string; the known values are listed for editor help only.
  outcome?: 'SUCCESS' | 'IN_PROGRESS' | 'ATTENTION' | 'OBSERVED' | (string & {});
  previousAttempt?: boolean;
  details?: Record<string, unknown>;
  // Staff only: the masked field changes a package edit made. Personal values arrive already masked
  // (a place, the last phone digits, or just "changed").
  changes?: IPackageOperationalChange[];
  // Internal portals only: the backend omits these for vendor callers.
  riderLatitude?: number;
  riderLongitude?: number;
  riderAccuracyMeters?: number;
  riderMockLocation?: boolean;
  riderLocationCapturedOn?: string;
  distanceFromDeliveryAddressKm?: number;
  distanceFromFacilityKm?: number;
  deliveryAddressPinnedByCustomer?: boolean;
  internalNote?: string;
  // Courier evidence and delivery proof: internal portals only, the backend never sends them to Nexus.
  courierAttemptNumber?: number;
  courierEventOn?: string;
  courierScanLocation?: string;
  courierScanLatitude?: number;
  courierScanLongitude?: number;
  proofOfDelivery?: string;
  courierAgent?: string;
  deliveryVerification?: string;
  labelScan?: string;
}

export interface IPackageOperationalChange {
  label: string;
  from?: string;
  to?: string;
}

export interface IPackageOperationalTimelinePhase {
  code: string;
  label: string;
  status?: 'COMPLETED' | 'CURRENT' | 'UPCOMING' | 'ATTENTION' | (string & {});
  current?: boolean;
  events: IPackageOperationalTimelineEvent[];
}

export interface IPackageOperationalTimeline {
  packageId: number;
  // A cancelled or legacy package can answer without a status, phase or truncation flag.
  currentStatus?: string;
  currentPhase?: string;
  generatedOn?: string;
  historyTruncated?: boolean;
  phases: IPackageOperationalTimelinePhase[];
}

// The return journey uses the package timeline's phases and events; a pickup attempt travels in the
// event's deliveryAttemptKey/Number/Label.
export interface IReturnOperationalTimeline {
  returnId: number;
  currentStatus?: string;
  currentPhase?: string;
  returnMode?: string;
  generatedOn?: string;
  historyTruncated?: boolean;
  phases: IPackageOperationalTimelinePhase[];
}

export enum PaymentGateway {
  CASH = 'CASH',
  CHANGE_RETURN = 'CHANGE_RETURN',
  PINE_LABS = 'PINE_LABS',
  MANUAL = 'MANUAL',
  CREDIT_NOTE = 'CREDIT_NOTE',
  GIFT_VOUCHER = 'GIFT_VOUCHER',
}

export enum PaymentMode {
  CASH = 'CASH',
  UPI = 'UPI',
  CREDIT_CARD = 'CREDIT_CARD',
  DEBIT_CARD = 'DEBIT_CARD',
  GIFT_VOUCHER = 'GIFT_VOUCHER',
  CREDIT_NOTE = 'CREDIT_NOTE',
}

export enum OrderPaymentStatus {
  INITIATED = 'INITIATED',
  VERIFIED = 'VERIFIED',
  DISCARDED = 'DISCARDED',
  FAILED = 'FAILED',
}

export interface IOrderPayment {
  id: number;
  cartId: string;
  salesChannelOrderId: string;
  salesStaffId: number;
  orderId: number;
  posTerminalId: number;
  paymentGateway: PaymentGateway;
  status: OrderPaymentStatus;
  pgTransactionId: string;
  amount: number;
  metadata?: { [key: string]: string };
  salesChannelId: number;
  paymentTerminalId: number;
  paymentMode: PaymentMode;
  remarks: string;
  cartTotalAmountToBePaid: number;
  cartMinAmountToBePaid: number;
  cartTotalAmountPaid: number;
  createdBy: string;
}

export enum ReturnMode {
  AT_STORE = 'AT_STORE',
  HOME_PICKUP = 'HOME_PICKUP',
}

export enum ReturnStatus {
  CREATED = 'CREATED',
  PICKUP_INITIATED = 'PICKUP_INITIATED',
  OUT_FOR_PICKUP = 'OUT_FOR_PICKUP',
  PICKED_UP = 'PICKED_UP',
  DROPPED_AT_FACILITY = 'DROPPED_AT_FACILITY',
  DELIVERED_TO_WAREHOUSE = 'DELIVERED_TO_WAREHOUSE',
  CANCELLED = 'CANCELLED',
  DECLINED = 'DECLINED',
  PICKUP_FAILED = 'PICKUP_FAILED',
}

export enum ReturnCourierExceptionType {
  CANCELLED_RETURN_AWB_MOVED = 'CANCELLED_RETURN_AWB_MOVED',
}

export enum ReturnCourierExceptionResolutionStatus {
  OPEN = 'OPEN',
  MIGRATED = 'MIGRATED',
  IGNORED = 'IGNORED',
  MANUAL_REVIEW = 'MANUAL_REVIEW',
  RESOLVED = 'RESOLVED',
}

export interface IReturnCourierException {
  id: number;
  exceptionType: ReturnCourierExceptionType;
  sourceReturnId: number;
  sourceAwb?: string;
  orderLineItemId: number;
  salesChannelOrderId?: string;
  skuCode?: string;
  sourceReturnStatus?: ReturnStatus;
  sourceReturnCancelledOn?: string;
  latestCourierStatus?: string;
  latestCourierBlobId?: number;
  latestCourierEventOn?: string;
  currentLineStatus?: string;
  currentLineReturnRequestId?: number;
  currentLineRefundRequestId?: number;
  targetReturnId?: number;
  targetReturnStatus?: ReturnStatus;
  resolutionStatus: ReturnCourierExceptionResolutionStatus;
  resolutionReason?: string;
  resolvedBy?: string;
  resolvedOn?: string;
  metadata?: Record<string, unknown>;
}

export interface IReturnCourierEvidenceEvent {
  eventOn?: string;
  concept?: string;
  status?: string;
  activity?: string;
  reason?: string;
  location?: string;
}

export interface IReturnCourierEvidenceAttempt {
  classification: string;
  courierPartner?: CourierPartner;
  courierName?: string;
  awb?: string;
  providerReference?: string;
  providerOrderId?: string;
  providerShipmentId?: string;
  setupStage?: string;
  failureCode?: string;
  retryable?: boolean;
  latestStatus?: string;
  latestActivity?: string;
  latestReason?: string;
  firstEvidenceOn?: string;
  latestEvidenceOn?: string;
  qcFailed?: boolean;
  qcImageUrls?: string[];
  events?: IReturnCourierEvidenceEvent[];
}

export interface IReturnCourierEvidenceConflict {
  code: string;
  message: string;
  courierPartner?: CourierPartner;
  awb?: string;
  providerOrderId?: string;
  observedOn?: string;
}

export interface IReturnCourierEvidence {
  returnId: number;
  currentIdentity?: {
    courierPartner?: CourierPartner;
    courierName?: string;
    awb?: string;
    providerOrderId?: string;
  };
  setupState: 'NOT_APPLICABLE' | 'PENDING' | 'FAILED' | 'READY';
  currentAttempt?: IReturnCourierEvidenceAttempt;
  previousAttempts?: IReturnCourierEvidenceAttempt[];
  conflicts?: IReturnCourierEvidenceConflict[];
  awaitingWarehouseReceipt?: boolean;
  historyTruncated?: boolean;
  evidenceCount?: number;
  /** Porter bookings for this return, newest first. */
  porterBookings?: Array<IPorterBookingEntry>;
  /** Banner text, e.g. after Porter cancelled the maximum number of attempts. */
  porterNotice?: string | null;
}

/**
 * Where the Porter customer point comes from. PIN_VERIFIED: the customer's pin is within 200 m of
 * where our rider delivered. PIN_MATCHES_ADDRESS: no past delivery there, but the customer placed
 * the pin and it matches the typed address. DELIVERY_LOCATION: the pin is farther, so we use our
 * delivery location. PIN_CHOSEN: the operator chose the customer's pin over our delivery location.
 * PIN_UNVERIFIED: older return quotes only (pin used as is, nothing to check it against).
 */
export type PorterLocationSource =
  | 'PIN_VERIFIED'
  | 'PIN_MATCHES_ADDRESS'
  | 'DELIVERY_LOCATION'
  | 'PIN_UNVERIFIED'
  | 'PIN_CHOSEN'
  | 'OPS_REVIEWED';

export type PorterBookingOutcome =
  | 'REQUESTED'
  | 'BOOKED'
  | 'REFUSED'
  | 'UNKNOWN'
  | 'CANCELLED_BY_PORTER'
  | 'CANCELLED_BY_US'
  | 'CANCEL_FAILED'
  | 'NOT_REBOOKED';

/** Fields shared by the return and package Porter quote previews. */
export interface IPorterQuoteBase {
  bookable: boolean;
  refusalReason: string | null;
  vehicle: string | null;
  fareInr: number | null;
  etaMinutes: number | null;
  fareCapInr: number;
  overCap: boolean;
  locationSource: PorterLocationSource | null;
  distanceFromDeliveryMeters: number | null;
  pinAvailable: boolean;
  deliveryLocationAvailable: boolean;
  nextAttempt: number;
  maxAttempts: number;
}

/** Fulfillment `GET /order/return/{id}/porter/quote` (read-only preview). */
export interface IPorterReturnQuote extends IPorterQuoteBase {
  returnId: number;
  dropFacilityId: number | null;
  dropFacilityName: string | null;
  estimatedDropAt: string | null;
  dropWindowWarning: string | null;
}

/** Fulfillment `GET /order/package/{id}/porter/quote` (read-only preview). */
export type PorterDeliveryPointEvidence = 'FOUND_ON_MAP' | 'CUSTOMER_CONFIRMED_CALL';

export interface IPorterPoint {
  lat: number;
  lng: number;
}

export interface IPorterDeliveryPointSelection extends IPorterPoint {
  id: number;
  evidence: PorterDeliveryPointEvidence;
  reason: string;
  actor: string | null;
  on: string;
  distanceFromAnchorMeters: number;
  status: 'SELECTED';
}

export type IPorterDeliveryPointHistory = Omit<
  IPorterDeliveryPointSelection,
  'lat' | 'lng' | 'status' | 'evidence'
> & {
  lat: number | null;
  lng: number | null;
  status: 'SELECTED' | 'CLEARED';
  distanceFromAnchorMeters: number | null;
  evidence: PorterDeliveryPointEvidence | null;
};

export interface IPorterDeliveryPointReview {
  supported: boolean;
  context: string;
  anchor: { lat: number | null; lng: number | null; computed: boolean | null };
  selectedPoint: IPorterDeliveryPointSelection | null;
  anyEvidenceMeters: number;
  customerConfirmedMeters: number;
  customerConfirmationRequired: boolean;
  canEdit: boolean;
  refusalReason?: string | null;
}

export interface IPorterDeliveryPointRequest extends IPorterPoint {
  evidence: PorterDeliveryPointEvidence;
  reason: string;
  context: string;
}

export interface IPorterPackageQuote extends IPorterQuoteBase {
  packageId: number;
  pickupFacilityId: number | null;
  pickupFacilityName: string | null;
  deliveryPointReview?: IPorterDeliveryPointReview | null;
  bookingAction?: 'NEW' | 'RESUME' | 'RESOLVE' | 'BLOCKED';
  bookingContext?: string | null;
}

/** Fulfillment `GET /order/package/{id}/porter/bookings`. */
export interface IPackagePorterBookings {
  porterBookings: Array<IPorterBookingEntry>;
  porterNotice: string | null;
  deliveryPointHistory?: IPorterDeliveryPointHistory[];
}

/** One Porter booking attempt (return courier evidence or package bookings), newest first. */
export interface IPorterBookingEntry {
  attempt: number;
  requestId: string;
  crn: string | null;
  outcome: PorterBookingOutcome;
  message: string | null;
  quotedFareInr: number | null;
  bookedFareInr: number | null;
  locationSource: string | null;
  distanceFromDeliveryMeters: number | null;
  trackingUrl: string | null;
  driverName: string | null;
  vehicleNumber: string | null;
  latestStatus: string | null;
  requestedOn: string | null;
  updatedOn: string | null;
  actor: string | null;
}

export interface IReshipRequest {
  id: number;
  createdOn: string;
  returnId: number;
  originalOrderLineItemId: number;
  idempotencyKey: string;
  unicomFacilityCode?: string;
  unicomSaleOrderCode?: string;
  status: OrderLineStatus;
}

export interface IReturnPickupPhoto {
  assetId: number;
  url?: string;
  capturedOn?: string;
}

export interface IReturnPickupPhotos {
  orderLineItemId: number;
  photos: IReturnPickupPhoto[];
}

export interface IReturnRequest {
  id: number;
  version?: number;
  isTestOrder?: boolean;
  orderId: number;
  returnMode: ReturnMode;
  customerName: string;
  customerPhone: string;

  pickupAddress?: IAddress;
  refundRequestId: number;
  status: ReturnStatus;
  returnReason: string;
  courierPartner?: CourierPartner;
  courierPartnerName?: string;
  courierTrackingId?: string;
  courierPartnerRequestId?: string;
  latestCourierStatus?: string;
  latestCourierActivity?: string;
  latestCourierReason?: string;
  latestCourierEventOn?: string;
  latestCourierQcFailed?: boolean;
  /** JIT B2B2C return dropped at the brand: when Fulfillment auto-confirms receipt (may be past until the job runs). */
  brandReceiptAutoConfirmOn?: string | null;
  /** Set only when Fulfillment auto-confirmed brand receipt (no brand confirmation within 7 days). */
  brandReceiptAutoConfirmedOn?: string | null;
  /** Response-only: the refund is released only after an independent receipt that has not happened yet. */
  refundAwaitsReceipt?: boolean;
  /** Server-derived: support approved this rider leg's location-bound action (rider action geofence). */
  geofenceExceptionApproved?: boolean;
  /** Server-derived: the rider step a location exception would cover now (RETURN_PICKUP or RETURN_DROP); null when none. */
  geofenceStep?: 'RETURN_PICKUP' | 'RETURN_DROP' | null;
  /** Fulfillment's verdict: CODE when the rider hands this return over by code, else NONE. */
  handoverMode?: HandoverMode;
  /** Hub reads only, while the approval is active: who approved it, why and when. */
  geofenceExceptionActorId?: string;
  geofenceExceptionReason?: string;
  geofenceExceptionAt?: string;
  /** The pickup address coordinate is not the customer's own pin; null when there is none. */
  pickupCoordinatesEstimated?: boolean | null;
  riderId?: number;
  pickupPhotos?: IReturnPickupPhotos[];
  pickupPhotosRecordedOn?: string;
  pickupPhotosRiderId?: number;
  trackingUrl?: string;
  unicomReversePickupCode?: string;
  returnToFacilityId?: number;
  salesChannelId?: number;
  createdOn: string;
  pickupDate: string;
  immediateAtDelivery?: boolean;
  jitB2b2cVendorId?: number;

  isExchange: boolean;
  exchangeOrderId: number;
  orderLineItemIds: Array<number>;
  returnItemPutawayDetails: Record<number, ReturnItemPutawayDetails>;
  unicomReturnEntries?: Array<IUnicomReturnEntry>;

  salesChannelOrderId: string;
  history?: Array<IReturnTrackingDetails>;
}

/** Server-owned pickup changes; no local inference from provider/status ages. */
export interface IReturnPickupChangeOperation {
  id: string | number;
  phase:
    | 'VALIDATING'
    | 'CANCELLING_OLD'
    | 'WAITING_PROVIDER_CANCELLATION'
    | 'BOOKING_REPLACEMENT'
    | 'REPLACEMENT_APPLIED'
    | 'REVIEW_REQUIRED'
    | 'PROVIDER_CANCELLATION_CHECK_FAILED'
    | 'CANCELLED_UNASSIGNED'
    | 'REPLACEMENT_APPLIED_PENDING_OLD_CANCELLATION'
    | 'ABANDONED_SAFE'
    | 'STOPPED_WITH_CUSTODY'
    | 'STOPPED_RETURN_ENDED'
    | 'PAUSED_CUSTOMER_NOT_READY';
  outcome?: string;
  message?: string;
  targetCourierPartner?: CourierPartner;
  targetMode?: 'AUTO_PORTER' | 'MANUAL' | 'CANCEL_PORTER';
  targetAwb?: string;
  reason?: string;
  pickupDate?: string;
  firstRequestedOn?: string;
  lastCheckedOn?: string;
  nextCheckOn?: string;
  warning?: string;
  escalationReason?: string;
  lastErrorCode?: string;
  recoveryAction?: string;
  retryAt?: string;
  canCheck: boolean;
  canConfirmReady?: boolean;
  canConfirmCancellationCharges?: boolean;
  canCloseSafely?: boolean;
  providerReviewRequired?: boolean;
  providerReviewCodes?: string[];
  unresolvedPorterRequestIds?: string[];
  canPause?: boolean;
  canRetryAfterReview?: boolean;
  canOverride: boolean;
  active: boolean;
}
export interface IShiprocketCancellationReview {
  cancellationEvidenceId?: number;
  cancellationOnly?: boolean;
  expectedCourierPartner?: CourierPartner;
  expectedProviderOrderId?: string;
  expectedAwb?: string;
  returnVersion?: number;
  canRetryAfterReview?: boolean;
  retryAt?: string;
  message?: string;
  outcome?: string;
}
export interface IReturnPickupChange {
  shiprocketCancellationReviews?: IShiprocketCancellationReview[];
  capabilities: {
    canChangePickup: boolean;
    canBookPorter: boolean;
    canAttachManual: boolean;
    canSyncSameBookingAwb: boolean;
    canOverridePendingCancellation: boolean;
    canConfirmReady?: boolean;
    canConfirmCancellationCharges?: boolean;
    acceptedDriverCancellationAcknowledgementRequired?: boolean;
    canCancelPorter?: boolean;
    canChangeFacility?: boolean;
    blockedFacilityReason?: string;
    blockedReason?: string;
  };
  currentOperation?: IReturnPickupChangeOperation | null;
  consequences?: string[];
  refundTiming?: { current: string; byTargetCourier: Partial<Record<CourierPartner, string>> };
}
export interface IPorterCancelReview {
  canRetryAfterReview?: boolean;
  reason?: string;
  operationId?: string | number;
  outcome?: string;
  message?: string;
  nextCheckOn?: string;
  canCancel: boolean;
  blockedReason?: string;
  owned: boolean;
  shared: boolean;
  accepted: boolean;
  started: boolean;
  expectedCrn?: string;
  warning?: string;
  readbackOnly?: boolean;
  retryAt?: string;
}

export interface IReturnPickupCoordinationOption {
  targetReturnId: number;
  targetReturnVersion: number;
  status: ReturnStatus;
  courierPartner: CourierPartner;
  courierPartnerName?: string;
  riderId?: number;
  pickupDate?: string;
}

export interface IUnicomReturnEntry {
  itemId: string;
  returnCode?: string;
  putawayCode?: string;
  success: boolean;
}

export interface IReturnTrackingDetails {
  status: ReturnStatus;
  remark: string;
  eventDate: string;
  icon: string;
  actorId?: number;
}

export interface ReturnItemPutawayDetails {
  qcPassed?: boolean;
  shelfCode: string;
  qcFailReason?: string;
  qcFailReasonLabel?: string;
  qcFailObservation?: string;
  qcFailedAssets?: Array<IAsset>;
}
